import { useState, useEffect } from 'react'
import {
    FloppyDiskIcon,
    ArrowCounterClockwiseIcon,
    PlusIcon,
    TrashIcon,
} from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopTextField,
    ScoopSelector,
    ScoopText,
    ScoopSpinner,
    ScoopTable,
    ScoopTooltip,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
} from '../../../../../ui'
import { FormField, FormSection } from '../shared'
import styles from './QueryPatternsForm.module.scss'

const PERIOD_OPTIONS = [
    { label: 'Daily', value: 'Daily' },
    { label: 'Weekly', value: 'Weekly' },
    { label: 'Monthly', value: 'Monthly' },
    { label: 'Quarterly', value: 'Quarterly' },
    { label: 'Annually', value: 'Annually' },
]

const CALENDAR_TYPE_OPTIONS = [
    { label: 'Calendar', value: 'Calendar' },
    { label: 'Rolling', value: 'Rolling' },
]

const EMPTY_PATTERN = {
    name: '',
    triggers: '',
    guidance: '',
}

// Reserved key for temporal defaults - has extra fields beyond triggers/guidance
const TEMPORAL_DEFAULTS_KEY = 'temporal_defaults'

/**
 * QueryPatternsForm - Form for editing query_patterns.json configuration
 *
 * @param {Object} data - Current query patterns content
 * @param {Function} onSave - Callback when saving (receives updated data)
 * @param {boolean} isSaving - Whether save is in progress
 * @param {boolean} isLoading - Whether data is loading
 */
export const QueryPatternsForm = ({ data, onSave, isSaving, isLoading, onDirtyStateChange }) => {
    const [patterns, setPatterns] = useState([])
    const [newPattern, setNewPattern] = useState({ ...EMPTY_PATTERN })
    const [temporalDefaults, setTemporalDefaults] = useState({
        triggers: '',
        default_period: 'Monthly',
        default_calendar_type: 'Calendar',
        guidance: '',
    })
    const [hasChanges, setHasChanges] = useState(false)

    // Initialize form data when data prop changes
    useEffect(() => {
        if (data) {
            // Patterns are at root level (except temporal_defaults which is special)
            // Each pattern has triggers and guidance fields
            const patternsArray = Object.entries(data)
                .filter(([key]) => key !== TEMPORAL_DEFAULTS_KEY)
                .map(([name, pattern]) => ({
                    name,
                    triggers: pattern.triggers || '',
                    guidance: pattern.guidance || '',
                }))

            setPatterns(patternsArray)

            // Temporal defaults has extra fields: default_period, default_calendar_type
            setTemporalDefaults({
                triggers: data.temporal_defaults?.triggers || '',
                default_period: data.temporal_defaults?.default_period || 'Monthly',
                default_calendar_type: data.temporal_defaults?.default_calendar_type || 'Calendar',
                guidance: data.temporal_defaults?.guidance || '',
            })

            setHasChanges(false)
        }
    }, [data])

    useEffect(() => {
        onDirtyStateChange?.(hasChanges)
    }, [hasChanges, onDirtyStateChange])

    const handlePatternChange = (index, field, value) => {
        setPatterns((prev) => {
            const updated = [...prev]
            updated[index] = { ...updated[index], [field]: value }
            return updated
        })
        setHasChanges(true)
    }

    const handleNewPatternChange = (field, value) => {
        setNewPattern((prev) => ({ ...prev, [field]: value }))
    }

    const handleAddPattern = () => {
        setPatterns((prev) => [...prev, { ...newPattern }])
        setNewPattern({ ...EMPTY_PATTERN })
        setHasChanges(true)
    }

    const handleRemovePattern = (index) => {
        setPatterns((prev) => prev.filter((_, i) => i !== index))
        setHasChanges(true)
    }

    const handleTemporalChange = (field, value) => {
        setTemporalDefaults((prev) => ({
            ...prev,
            [field]: value,
        }))
        setHasChanges(true)
    }

    const handleSave = () => {
        // Build updated data with patterns at root level
        const updatedData = {
            // Start with temporal_defaults (special pattern with extra fields)
            temporal_defaults: {
                triggers: temporalDefaults.triggers,
                default_period: temporalDefaults.default_period,
                default_calendar_type: temporalDefaults.default_calendar_type,
                guidance: temporalDefaults.guidance,
            },
        }

        // Add other patterns at root level
        patterns.forEach((pattern) => {
            if (pattern.name.trim()) {
                updatedData[pattern.name.trim()] = {
                    triggers: pattern.triggers,
                    guidance: pattern.guidance,
                }
            }
        })

        onSave(updatedData)
        setHasChanges(false)
    }

    const handleReset = () => {
        if (data) {
            // Patterns are at root level (except temporal_defaults)
            const patternsArray = Object.entries(data)
                .filter(([key]) => key !== TEMPORAL_DEFAULTS_KEY)
                .map(([name, pattern]) => ({
                    name,
                    triggers: pattern.triggers || '',
                    guidance: pattern.guidance || '',
                }))

            setPatterns(patternsArray)

            setTemporalDefaults({
                triggers: data.temporal_defaults?.triggers || '',
                default_period: data.temporal_defaults?.default_period || 'Monthly',
                default_calendar_type: data.temporal_defaults?.default_calendar_type || 'Calendar',
                guidance: data.temporal_defaults?.guidance || '',
            })

            setNewPattern({ ...EMPTY_PATTERN })
            setHasChanges(false)
        }
    }

    const isNewPatternValid = newPattern.name.trim() !== ''

    const columns = [
        {
            field: 'name',
            header: 'Pattern Name',
            width: 150,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopTextField
                    value={value || ''}
                    onChange={(e) => handlePatternChange(rowIndex, 'name', e.target.value)}
                    placeholder="Pattern Name"
                    disabled={isSaving}
                />
            ),
        },
        {
            field: 'triggers',
            header: 'Triggers',
            width: 200,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopTextField
                    value={value || ''}
                    onChange={(e) => handlePatternChange(rowIndex, 'triggers', e.target.value)}
                    placeholder="keyword1 | keyword2 | keyword3"
                    disabled={isSaving}
                />
            ),
        },
        {
            field: 'guidance',
            header: 'Guidance',
            flex: 1,
            sortable: false,
            render: ({ value, rowIndex }) => (
                <ScoopTooltip
                    title={value || ''}
                    placement="top"
                    className={styles.guidanceTooltip}
                >
                    <div className={styles.guidanceWrapper}>
                        <ScoopTextField
                            value={value || ''}
                            onChange={(e) =>
                                handlePatternChange(rowIndex, 'guidance', e.target.value)
                            }
                            placeholder="Guidance"
                            multiline
                            minRows={1}
                            maxRows={1}
                            disabled={isSaving}
                            className={styles.guidanceInput}
                        />
                    </div>
                </ScoopTooltip>
            ),
        },
        {
            field: 'actions',
            header: ' ',
            width: 48,
            sortable: false,
            resizable: false,
            render: ({ rowIndex }) => (
                <ScoopButton
                    variant="grey"
                    size="xs"
                    onClick={() => handleRemovePattern(rowIndex)}
                    disabled={isSaving}
                    className={styles.deleteButton}
                >
                    <TrashIcon size={16} />
                </ScoopButton>
            ),
        },
    ]

    if (isLoading) {
        return (
            <ScoopCenter className={styles.loading}>
                <ScoopSpinner size={32} />
                <ScoopText>Loading query patterns...</ScoopText>
            </ScoopCenter>
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <ScoopText as="h2" className={styles.title}>
                        Query Patterns
                    </ScoopText>
                    <ScoopText className={styles.description}>
                        Define patterns that help the AI understand how to construct queries for
                        specific types of analysis.
                    </ScoopText>
                </div>
                <ScoopRow className={styles.actions}>
                    {hasChanges && (
                        <ScoopButton
                            variant="grey"
                            size="sm"
                            onClick={handleReset}
                            disabled={isSaving}
                            leftSection={<ArrowCounterClockwiseIcon size={16} />}
                        >
                            Reset
                        </ScoopButton>
                    )}
                    <ScoopButton
                        variant="default"
                        size="sm"
                        onClick={handleSave}
                        disabled={!hasChanges || isSaving}
                        loading={isSaving}
                        leftSection={<FloppyDiskIcon size={16} />}
                    >
                        Save Changes
                    </ScoopButton>
                </ScoopRow>
            </div>

            <div className={styles.content}>
                {/* Query Patterns Section */}
                <FormSection
                    title="Query Patterns"
                    description="Define named patterns with trigger keywords. When users ask questions containing these triggers, the associated guidance helps the AI construct appropriate queries."
                >
                    <div className={styles.tableWrapper}>
                        <ScoopTable
                            columns={columns}
                            rows={patterns}
                            emptyMessage="No patterns defined. Add a pattern below to get started."
                            sorting={{ enabled: false }}
                            pagination={{ enabled: false }}
                            resizable={false}
                            domLayout="autoHeight"
                            rowHeight={52}
                        />
                    </div>

                    <div className={styles.inputRow}>
                        <div className={styles.inputRowFields}>
                            <ScoopTextField
                                value={newPattern.name}
                                onChange={(e) => handleNewPatternChange('name', e.target.value)}
                                placeholder="Pattern Name"
                                disabled={isSaving}
                            />
                            <ScoopTextField
                                value={newPattern.triggers}
                                onChange={(e) => handleNewPatternChange('triggers', e.target.value)}
                                placeholder="keyword1 | keyword2 | keyword3"
                                disabled={isSaving}
                            />
                            <ScoopTextField
                                value={newPattern.guidance}
                                onChange={(e) => handleNewPatternChange('guidance', e.target.value)}
                                placeholder="Guidance"
                                multiline
                                minRows={1}
                                disabled={isSaving}
                                className={styles.inputRowGuidance}
                            />
                        </div>
                        <ScoopButton
                            variant="grey"
                            size="sm"
                            onClick={handleAddPattern}
                            disabled={isSaving || !isNewPatternValid}
                            leftSection={<PlusIcon size={16} />}
                            className={styles.addButton}
                        >
                            Add Pattern
                        </ScoopButton>
                    </div>
                </FormSection>

                {/* Temporal Defaults Section */}
                <FormSection
                    title="Temporal Defaults"
                    description="Configure default settings for time-based queries. These defaults are used when users ask about trends or time periods."
                >
                    <ScoopColumn className={styles.fields}>
                        <FormField
                            label="Trigger Keywords"
                            helpText="Keywords that indicate a temporal query (pipe-separated)"
                        >
                            <ScoopTextField
                                value={temporalDefaults.triggers}
                                onChange={(e) => handleTemporalChange('triggers', e.target.value)}
                                placeholder="trend | over time | history | compare"
                                disabled={isSaving}
                            />
                        </FormField>

                        <ScoopRow className={styles.selectRow}>
                            <FormField
                                label="Default Period"
                                helpText="Default time granularity for temporal queries"
                            >
                                <ScoopSelector
                                    value={temporalDefaults.default_period}
                                    onChange={(e) =>
                                        handleTemporalChange('default_period', e.target.value)
                                    }
                                    options={PERIOD_OPTIONS}
                                    disabled={isSaving}
                                />
                            </FormField>

                            <FormField
                                label="Calendar Type"
                                helpText="Whether to use calendar or rolling periods"
                            >
                                <ScoopSelector
                                    value={temporalDefaults.default_calendar_type}
                                    onChange={(e) =>
                                        handleTemporalChange(
                                            'default_calendar_type',
                                            e.target.value
                                        )
                                    }
                                    options={CALENDAR_TYPE_OPTIONS}
                                    disabled={isSaving}
                                />
                            </FormField>
                        </ScoopRow>

                        <FormField
                            label="Guidance"
                            helpText="Instructions for handling temporal queries"
                        >
                            <ScoopTextField
                                value={temporalDefaults.guidance}
                                onChange={(e) => handleTemporalChange('guidance', e.target.value)}
                                placeholder="Explain how to interpret and construct temporal queries..."
                                multiline
                                rows={3}
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopColumn>
                </FormSection>
            </div>
        </div>
    )
}
